/**
 * Os catálogos do projeto: componentes, conectores, cores, chicotes, restrições
 * e modelos.
 *
 * **Regra dura 1: carrega uma vez por projeto, por sessão.** `staleTime`
 * infinito com o `projetoId` na chave — trocar de projeto troca a chave, voltar
 * ao mesmo projeto não gera requisição nenhuma. O servidor de banco está no
 * limite e o front não pode acrescentar carga.
 *
 * Circuito **não** está aqui de propósito: são milhares por projeto e a página é
 * de 200, então ele é busca no servidor (regra 2) e mora no campo de busca do
 * lançamento, não num catálogo em memória.
 */

import { useQuery } from "@tanstack/react-query"
import { obter } from "./cliente"
import { catalogoPorProjeto, chaves, POR_PAGINA } from "./query"
import type {
  ChicoteResponse, ComponenteResponse, ConectorResponse, CorResponse,
  PaginaResponse, RestricaoResponse,
} from "./tipos"

/**
 * O que um filtro de multi-seleção precisa saber sobre um item. O id é número
 * nos cadastros; nos campos de texto do pinout (pino, observação, fonte) o
 * próprio texto é o valor.
 */
export type ItemDeCatalogo<K extends number | string = number> = {
  id: K
  codigo?: string
  rotulo: string
  /** Quantos pinouts usam. Vem pronto no `qtd` — ordena a lista pelo mais usado. */
  usos: number
}

function porUso<K extends number | string>(a: ItemDeCatalogo<K>, b: ItemDeCatalogo<K>): number {
  return b.usos - a.usos || a.rotulo.localeCompare(b.rotulo, "pt-BR")
}

/**
 * Um catálogo cabe em memória quando `total <= porPagina`. Quando não cabe, o
 * que temos é a primeira página — a lista fica incompleta e quem mostra precisa
 * dizer isso, em vez de fingir que aqueles são todos.
 */
export type Catalogo<K extends number | string = number> = {
  itens: ItemDeCatalogo<K>[]
  total: number
  completo: boolean
  carregando: boolean
  /** A lista não veio (API fora, endpoint que não existe). Não é o mesmo que "não há nada". */
  erro?: boolean
  /**
   * Busca no servidor — só nos catálogos que podem passar da página (componente
   * e conector). Quem mostra usa quando `completo` é falso: filtrar só a página
   * em memória escondia justamente o que ficou de fora dela.
   */
  buscar?: (termo: string) => Promise<ItemDeCatalogo<K>[]>
  /**
   * De onde vem a lista, para o cache da busca no servidor. Sem ela, a busca é
   * guardada pelo rótulo do filtro — o que basta quando a lista não muda com os
   * outros filtros.
   */
  chave?: readonly unknown[]
}

function usarCatalogo<T>(
  chave: readonly unknown[],
  rota: string,
  params: Record<string, unknown>,
  mapear: (x: T) => ItemDeCatalogo,
  ligado = true,
): Catalogo {
  const { data, isPending } = useQuery({
    queryKey: chave,
    enabled: ligado,
    ...catalogoPorProjeto,
    queryFn: () => obter<PaginaResponse<T>>(rota, { ...params, pagina: 1 }),
  })

  const itens = (data?.itens ?? []).map(mapear).sort(porUso)
  const total = data?.total ?? 0
  return { itens, total, completo: total <= POR_PAGINA, carregando: isPending }
}

/** A busca por pedaço da API (`busca`), devolvendo no formato do catálogo. */
function buscaNoServidor<T>(rota: string, params: Record<string, unknown>, mapear: (x: T) => ItemDeCatalogo) {
  return async (termo: string) =>
    (await obter<PaginaResponse<T>>(rota, { ...params, busca: termo, pagina: 1 })).itens.map(mapear).sort(porUso)
}

const componenteDoCatalogo = (c: ComponenteResponse): ItemDeCatalogo =>
  ({ id: c.id, codigo: c.codigo, rotulo: c.descricao || c.codigo, usos: c.qtd })

export function usarComponentes(projetoId: number): Catalogo {
  const params = { projetoId, ordem: "codigo" }
  return {
    ...usarCatalogo<ComponenteResponse>(
      chaves.componentes(projetoId), "api/componentes", params, componenteDoCatalogo, projetoId > 0),
    buscar: buscaNoServidor("api/componentes", params, componenteDoCatalogo),
  }
}

const conectorDoCatalogo = (c: ConectorResponse): ItemDeCatalogo =>
  ({ id: c.id, rotulo: c.apelido || c.descricao, usos: c.qtd })

export function usarConectores(projetoId: number, componenteId?: number): Catalogo {
  const params = { projetoId, componentes: componenteId || undefined }
  return {
    ...usarCatalogo<ConectorResponse>(
      chaves.conectores(projetoId, componenteId), "api/conectores", params, conectorDoCatalogo, projetoId > 0),
    buscar: buscaNoServidor("api/conectores", params, conectorDoCatalogo),
  }
}

export function usarCores(projetoId: number): Catalogo {
  return usarCatalogo<CorResponse>(
    chaves.cores(projetoId), "api/cores-projeto", { projetoId, ordem: "descricao" },
    (c) => ({ id: c.id, rotulo: c.descricao, usos: c.qtd }),
    projetoId > 0,
  )
}

export function usarChicotes(projetoId: number): Catalogo {
  return usarCatalogo<ChicoteResponse>(
    chaves.chicotes(projetoId), "api/chicotes", { projetoId, ordem: "descricao" },
    (c) => ({ id: c.id, rotulo: c.descricao, usos: c.qtd }),
    projetoId > 0,
  )
}

export function usarRestricoes(projetoId: number): Catalogo {
  return usarCatalogo<RestricaoResponse>(
    chaves.restricoes(projetoId), "api/restricoes", { projetoId, ordem: "descricao" },
    (c) => ({ id: c.id, rotulo: c.descricao, usos: c.qtd }),
    projetoId > 0,
  )
}

/**
 * `GET api/modelos` é a exceção do módulo: devolve **array puro**, não
 * `PaginaResponse`. Vem do `ModeloDB.listar_projeto` do legado, que sempre
 * devolveu a lista inteira — um projeto tem dezenas de modelos, não milhares.
 * Tratar como página aqui daria `itens: undefined` e uma lista vazia calada.
 */
type ModeloResponse = {
  id: number
  projetoId: number
  descricao: string
  veiculo: string
  motor: string
  publicado: boolean
}

/** Modelos do projeto. Filtrar por modelo aplica a RN-01 — não é só um filtro. */
export function usarModelos(projetoId: number): Catalogo {
  const { data, isPending } = useQuery({
    queryKey: ["catalogo", "modelos", projetoId] as const,
    enabled: projetoId > 0,
    ...catalogoPorProjeto,
    queryFn: () => obter<ModeloResponse[]>("api/modelos", { projetoId }),
  })
  const itens = (data ?? []).map((m) => ({
    id: m.id,
    rotulo: m.descricao || `${m.veiculo} ${m.motor}`.trim(),
    usos: 0,
  }))
  // Sem paginação, a lista é sempre inteira.
  return { itens, total: itens.length, completo: true, carregando: isPending }
}
